import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { customersApi } from '../../../api/customers.js';
import { ApiError } from '../../../api/client.js';
import { fmt } from '../../../lib/formatters.js';

const INVITE_STATUS = {
  pending: { label: 'In attesa', tone: 'warning' },
  claimed: { label: 'Attivato', tone: 'success' },
  expired: { label: 'Scaduto', tone: 'neutral' },
  revoked: { label: 'Revocato', tone: 'neutral' }
};

/**
 * Generate a strong temporary password that satisfies the server policy
 * (>= 12 chars, at least one lowercase, uppercase and digit). Uses the Web
 * Crypto RNG and guarantees one character from each required class.
 */
function generatePassword(length = 16) {
  const lower = 'abcdefghijkmnpqrstuvwxyz';
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const digits = '23456789';
  const symbols = '!@#$%&*?';
  const all = lower + upper + digits + symbols;
  const pick = (set) => set[crypto.getRandomValues(new Uint32Array(1))[0] % set.length];
  const out = [pick(lower), pick(upper), pick(digits), pick(symbols)];
  while (out.length < length) out.push(pick(all));
  // Fisher-Yates shuffle so the guaranteed classes are not always in front.
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = crypto.getRandomValues(new Uint32Array(1))[0] % (i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out.join('');
}

/**
 * Staff portal-linking modal for a customer. Two paths:
 *  - Send an email invite (claim link). The returned claim URL is shown so the
 *    operator can also share it manually when email is not configured.
 *  - Provision/link a portal account directly from an email and a temporary
 *    password.
 */
export function CustomerLinkModal({ open, onClose, customer }) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState((customer && customer.email) || '');
  const [password, setPassword] = useState('');
  const [claimUrl, setClaimUrl] = useState(null);

  const customerId = customer && customer._id;

  const invitesQuery = useQuery({
    queryKey: ['customer', customerId, 'invites'],
    queryFn: () => customersApi.invites(customerId),
    enabled: open && Boolean(customerId)
  });
  const invites = (invitesQuery.data && invitesQuery.data.items) || [];

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
    queryClient.invalidateQueries({ queryKey: ['customer', customerId, 'invites'] });
  };

  const inviteMut = useMutation({
    mutationFn: () => customersApi.invite(customerId),
    onSuccess: (data) => {
      setClaimUrl(data.claimUrl);
      toast.success(data.emailSent ? 'Invito inviato via email' : 'Invito creato: condividi il link');
      refresh();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Invio invito non riuscito');
    }
  });

  const revokeMut = useMutation({
    mutationFn: (inviteId) => customersApi.revokeInvite(customerId, inviteId),
    onSuccess: () => { toast.success('Invito eliminato'); refresh(); },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Eliminazione non riuscita')
  });

  const linkMut = useMutation({
    mutationFn: () => customersApi.link(customerId, { email: email.trim(), password: password || undefined }),
    onSuccess: () => {
      toast.success('Account collegato');
      refresh();
      onClose();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Collegamento non riuscito');
    }
  });

  const copy = async () => {
    try { await navigator.clipboard.writeText(claimUrl); toast.success('Link copiato'); }
    catch { /* clipboard unavailable */ }
  };

  const generate = () => setPassword(generatePassword());

  const copyPassword = async () => {
    try { await navigator.clipboard.writeText(password); toast.success('Password copiata'); }
    catch { /* clipboard unavailable */ }
  };

  return (
    <Modal open={open} onClose={onClose} title="Collega al portale" size="md">
      <div className="space-y-6">
        <section>
          <h3 className="text-sm font-semibold text-ink">Invia invito via email</h3>
          <p className="mt-1 text-sm text-ink-muted">
            Il cliente riceve un link per attivare il proprio accesso. Serve un'email sul profilo.
          </p>
          <Button className="mt-3" variant="secondary" onClick={() => inviteMut.mutate()} disabled={inviteMut.isPending}>
            {inviteMut.isPending ? 'Invio…' : 'Invia invito'}
          </Button>
          {claimUrl && (
            <div className="mt-3 rounded-md bg-surface-subtle p-3">
              <p className="text-xs uppercase tracking-wide text-ink-faint">Link di attivazione</p>
              <p className="mt-1 break-all text-sm text-ink">{claimUrl}</p>
              <button type="button" onClick={copy} className="mt-2 text-sm text-brand hover:text-brand-hover">
                Copia link
              </button>
            </div>
          )}

          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Inviti creati</p>
            {invitesQuery.isLoading && <p className="mt-1 text-sm text-ink-muted">Carico…</p>}
            {invitesQuery.isSuccess && invites.length === 0 && (
              <p className="mt-1 text-sm text-ink-muted">Nessun invito ancora creato.</p>
            )}
            {invites.length > 0 && (
              <ul className="mt-2 divide-y divide-border rounded-md border border-border">
                {invites.map((inv) => {
                  const meta = INVITE_STATUS[inv.status] || { label: inv.status, tone: 'neutral' };
                  return (
                    <li key={inv.id} className="flex items-center justify-between gap-3 px-3 py-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm text-ink">{inv.email || '-'}</p>
                        <p className="text-xs text-ink-muted">
                          Creato {fmt.shortDate(inv.createdAt)}
                          {inv.status === 'pending' && inv.expiresAt ? ` · scade ${fmt.shortDate(inv.expiresAt)}` : ''}
                          {inv.status === 'claimed' && inv.claimedAt ? ` · attivato ${fmt.shortDate(inv.claimedAt)}` : ''}
                        </p>
                        {inv.status === 'pending' && (
                          <div className="mt-1 flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => inviteMut.mutate()}
                              disabled={inviteMut.isPending}
                              className="text-xs text-brand hover:text-brand-hover disabled:opacity-50"
                              title="Genera un nuovo link (sostituisce il precedente) e mostralo qui sopra"
                            >
                              Rigenera link
                            </button>
                            <button
                              type="button"
                              onClick={() => revokeMut.mutate(inv.id)}
                              disabled={revokeMut.isPending}
                              className="text-xs text-danger hover:opacity-80 disabled:opacity-50"
                            >
                              Elimina
                            </button>
                          </div>
                        )}
                      </div>
                      <Badge tone={meta.tone}>{meta.label}</Badge>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>

        <div className="border-t border-border" />

        <section>
          <h3 className="text-sm font-semibold text-ink">Collega direttamente</h3>
          <p className="mt-1 text-sm text-ink-muted">
            Crea o collega un account portale. Per un nuovo account indica una password temporanea (min. 12 caratteri).
          </p>
          <div className="mt-3 space-y-3">
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@esempio.it"
            />
            <div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Input
                    label="Password temporanea"
                    type="text"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Solo per un nuovo account"
                  />
                </div>
                <Button type="button" variant="secondary" onClick={generate}>Genera</Button>
              </div>
              {password && (
                <button type="button" onClick={copyPassword} className="mt-2 text-sm text-brand hover:text-brand-hover">
                  Copia password
                </button>
              )}
            </div>
            <Button onClick={() => linkMut.mutate()} disabled={linkMut.isPending || !email.trim()}>
              {linkMut.isPending ? 'Collego…' : 'Collega account'}
            </Button>
          </div>
        </section>
      </div>
    </Modal>
  );
}
